import { ModalService, SimpleMessageService, Tenant, WebAppCommonModule } from "@allbesmart/web-app-common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { first, Subscription } from "rxjs";
import { OrderDto, PutOrderDto } from "src/app/models/order-dto";
import { OrderService } from "src/app/services/order.service";
import { TenantManagementService } from "src/app/services/tenant-management.service";
import { TenantInfoComponent } from "../tenant-info/tenant-info.component";

@Component({
    selector: "oaibox-order-tenant-assignment",
    templateUrl: "./order-tenant-assignment.component.html",
    styleUrl: "./order-tenant-assignment.component.css",
    imports: [
        WebAppCommonModule,
        TenantInfoComponent,
        ReactiveFormsModule,
    ],
})
export class OrderTenantAssignmentComponent implements OnInit, OnDestroy {
  @Input({ required: true }) order!: OrderDto;

  protected searchInputControl = new FormControl<string | null>(null);

  private tenants: Tenant[] = [];
  protected shownTenants: Tenant[] = [];
  protected selectedTenants: Tenant[] = [];

  private tenantsSubscription?: Subscription;
  private readonly querySubscription?: Subscription;
  private updateOrderSubscription?: Subscription;

  constructor(
    private readonly tenantManagementService: TenantManagementService,
    private readonly orderService: OrderService,
    private readonly modalService: ModalService,
    private readonly simpleMessageService: SimpleMessageService
  ) {
    this.querySubscription = this.searchInputControl.valueChanges.subscribe(
      (value) => {
        const q = (value ?? "").trim().toLowerCase();

        this.shownTenants = this.tenants.filter(
          (tenant) =>
            tenant.clientName.toLowerCase().includes(q) ||
            tenant.clientDescription.toLowerCase().includes(q) ||
            tenant.id.toLowerCase().includes(q)
        );
      }
    );
  }

  ngOnInit(): void {
    this.tenantsSubscription = this.tenantManagementService
      .getTenants$()
      .pipe(first())
      .subscribe((tenants) => {
        this.tenants = tenants;
        this.shownTenants = tenants;
        this.getAssignedTenants();
      });
    this.tenantManagementService.refreshTenants();
  }

  private getAssignedTenants(): void {
    this.order.tenantIds?.forEach((tenantId) => {
      const tenant = this.tenantManagementService.getTenantByTenantId(tenantId);

      if (tenant && !this.selectedTenants.includes(tenant)) {
        this.selectedTenants.push(tenant);
      }
    });
  }

  protected onSelectTenant(tenant: Tenant): void {
    if (!this.selectedTenants.includes(tenant))
      this.selectedTenants.push(tenant);
    else
      this.selectedTenants = this.selectedTenants.filter((t) => t !== tenant);
  }

  protected assignTenant(): void {
    const tenantsIds = this.selectedTenants.map((tenant) => tenant.id);

    const putOrderDto: PutOrderDto = {
      id: this.order.id,
      bom: this.order.bom,
      tenantIds: tenantsIds,
    };

    this.updateOrderSubscription?.unsubscribe();
    this.updateOrderSubscription = this.orderService
      .updateOrder(putOrderDto)
      .subscribe({
        next: () => {
          this.orderService.refreshOrders();
          this.simpleMessageService.showSimpleMessage({
            message: "Tenants assigned successfully!",
            icon: "fa-solid fa-check-double",
            color: "success",
          });
          this.modalService.popModal();
        },
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to assign Tenants!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  protected closeModal(): void {
    this.modalService.popModal();
  }

  ngOnDestroy(): void {
    this.tenantsSubscription?.unsubscribe();
    this.querySubscription?.unsubscribe();
    this.updateOrderSubscription?.unsubscribe();
  }
}
